import React, { useRef, useState } from "react";
import { PackageSearch, FileSpreadsheet } from "lucide-react";
import CrudModule from "@/components/CrudModule";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LegalCompliance } from "@/components/LegalCompliance";

function CatalogImport({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const { canEdit } = useAuth();
  if (!canEdit) return null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ppe-catalog/import-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      toast.success(`${data.imported} EPI(s) importado(s) para o catálogo`);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na importação da planilha");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()}
        className="border-[#134074]/30 text-[#134074] hover:bg-[#134074]/5" data-testid="ppe-catalog-import-btn">
        <FileSpreadsheet className="mr-1.5 h-4 w-4" /> {busy ? "Importando..." : "Importar planilha (XLSX/CSV)"}
      </Button>
      <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid="ppe-catalog-import-input" />
    </>
  );
}

export default function PpeCatalog() {
  const [k, setK] = useState(0);
  return (
    <CrudModule
      key={k}
      title="Catálogo de EPIs (NR-06)"
      subtitle="Cadastro central de EPIs com CA e validade — alimenta a busca da entrega de EPI"
      icon={PackageSearch}
      endpoint="ppe-catalog"
      exportKey="ppe-catalog"
      testid="ppe-catalog"
      headerExtra={<><LegalCompliance /><CatalogImport onDone={() => setK((x) => x + 1)} /></>}
      columns={[
        { key: "name", label: "EPI" },
        { key: "ca_number", label: "CA" },
        { key: "ca_expiry", label: "Validade CA", badge: "validity" },
        { key: "product_expiry", label: "Validade produto", badge: "validity" },
        { key: "risk_category", label: "Cat. risco" },
        { key: "complement_ppe", label: "Complementar" },
        { key: "manufacturer", label: "Fabricante" },
        { key: "category", label: "Categoria" },
        { key: "size", label: "Tamanho" },
      ]}
      fields={[
        { name: "name", label: "EPI (descrição)", type: "text", required: true, full: true },
        { name: "ca_number", label: "Número do CA", type: "text", required: true },
        { name: "ca_expiry", label: "Validade do CA (MTE)", type: "date" },
        { name: "product_expiry", label: "Validade do produto (fabricante/lote)", type: "date" },
        { name: "risk_category", label: "Categoria de risco (Port. 1.369/2024)", type: "select", options: ["I", "II", "III"], default: "I" },
        { name: "complement_ppe", label: "Item complementar obrigatório (ex.: Talabarte)", type: "text" },
        { name: "esocial_description", label: "Descrição conforme manual eSocial", type: "text", full: true },
        { name: "manufacturer", label: "Fabricante / Modelo", type: "text" },
        { name: "category", label: "Categoria", type: "select", options: ["Proteção da Cabeça", "Proteção Auditiva", "Proteção Respiratória", "Proteção Visual/Facial", "Proteção de Mãos e Braços", "Proteção de Pés", "Proteção do Tronco", "Proteção contra Quedas", "Vestimentas", "Outros"], default: "Outros" },
        { name: "size", label: "Tamanho", type: "text" },
        { name: "description", label: "Observações", type: "textarea", full: true },
      ]}
    />
  );
}
